import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { useSeo, JsonLd } from "@/lib/seo";
import { api } from "@/lib/api";
import RentalRequestForm from "@/components/location/RentalRequestForm";
import { ItemCard, priceLine, photoUrl, DepositBlock } from "@/pages/Location";


function List({ title, items, testid }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#DCB457] mb-3">{title}</p>
      <ul className="space-y-2">{items.map((f) => <li key={typeof f === "string" ? f : f.name} className="flex gap-2.5 text-sm text-white/75"><Check size={14} className="text-[#DCB457] mt-0.5 shrink-0" />{typeof f === "string" ? f : <span>{f.name}{f.price_ttc ? <span className="text-white/45"> — {f.price_label || `${Math.round(f.price_ttc)} €`}</span> : null}</span>}</li>)}</ul>
    </div>
  );
}

export default function LocationItem() {
  const { slug } = useParams();
  const [it, setIt] = useState(null);
  const [all, setAll] = useState([]);
  const [err, setErr] = useState(false);
  const [photo, setPhoto] = useState(0);
  useEffect(() => {
    setIt(null);
    api.get(`/location/items/${slug}`).then((r) => setIt(r.data)).catch(() => setErr(true));
    api.get("/location/catalog").then((r) => setAll(r.data.items)).catch(() => {});
  }, [slug]);
  useSeo({
    title: it ? `Location ${it.name} — Nord & Lille | My Animation` : "Location matériel | My Animation",
    description: it ? `${it.short_description} Location dans le Nord, à Lille et en Hauts-de-France, ${priceLine(it)}.` : "",
    path: `/location/${slug}`,
  });
  if (err) return <div className="pt-40 pb-24 text-center"><p className="text-white/60">Ce matériel n'est plus disponible.</p><Link to="/location" className="btn-ghost-gold mt-6 inline-flex">Voir le catalogue</Link></div>;
  if (!it) return <div className="pt-40 pb-24 text-center text-white/45 italic">Chargement…</div>;
  const photos = it.photos || [];
  return (
    <>
      <JsonLd data={{ "@context": "https://schema.org", "@type": "Product", name: it.name, description: it.short_description,
        image: photos.map((p) => photoUrl(p.url)), brand: { "@type": "Brand", name: "My Animation" },
        ...(it.price_from ? { offers: { "@type": "Offer", priceCurrency: "EUR", price: it.price_from, availability: "https://schema.org/InStock" } } : {}) }} />
      <section className="pt-32 md:pt-40 pb-16 px-6 md:px-10 lg:px-14 max-w-[1300px] mx-auto" data-testid="loc-item-page">
        <Link to="/location" className="text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-[#DCB457]">← Location de matériel</Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mt-8">
          <div>
            <div className="aspect-[4/3] card-luxe !p-0 overflow-hidden">
              {photos.length ? <img src={photoUrl(photos[photo].url)} alt={it.name} className="w-full h-full object-cover" data-testid="loc-item-photo" />
                : <div className="w-full h-full flex items-center justify-center font-serif-luxe text-4xl text-[#DCB457]/40">{it.name}</div>}
            </div>
            {photos.length > 1 && <div className="flex gap-2 mt-3">{photos.map((p, i) => <button key={p.id} onClick={() => setPhoto(i)} className={`w-20 h-16 overflow-hidden border ${i === photo ? "border-[#DCB457]" : "border-white/10"}`}><img src={photoUrl(p.url)} alt="" className="w-full h-full object-cover" /></button>)}</div>}
          </div>
          <div>
            <p className="eyebrow">{it.category?.name}</p>
            <h1 className="font-serif-luxe text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]" data-testid="loc-item-name">{it.name}</h1>
            <p className="text-white/70 mt-5 leading-relaxed">{it.short_description}</p>
            {it.description && <p className="text-white/55 mt-3 text-sm leading-relaxed whitespace-pre-line">{it.description}</p>}
            <div className="card-luxe p-5 mt-7 space-y-1" data-testid="loc-item-pricing">
              <p className="font-serif-luxe text-3xl gold-text">Location : {priceLine(it)}{it.price && it.price_unit ? <span className="text-sm text-white/50"> / {it.price_unit}</span> : null}</p>
              {it.package_label && <p className="text-sm text-white/65">{it.package_label}</p>}
              {it.rental_duration && <p className="text-sm text-white/65">Durée : {it.rental_duration}</p>}
              {it.min_amount ? <p className="text-xs text-white/45">Montant minimum de location : {Math.round(it.min_amount)} €</p> : null}
              <p className="text-xs text-white/45">{[it.pickup_possible && "Retrait possible", it.delivery_possible && `Livraison possible${it.delivery_price ? ` (${Math.round(it.delivery_price)} €)` : ""}`].filter(Boolean).join(" · ")}{it.availability_note ? ` · ${it.availability_note}` : ""}</p>
              <DepositBlock i={it} testid="loc-item-deposit" />
            </div>
            <a href="#demande" className="btn-gold mt-6 inline-flex" data-testid="loc-item-cta">Demander un devis</a>
          </div>
        </div>
        {it.packages?.length > 0 && (
          <div className="mt-16" data-testid="loc-item-packages">
            <h2 className="font-serif-luxe text-3xl md:text-4xl">Forfaits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
              {it.packages.map((p) => (
                <div key={p.id} className="card-luxe p-6"><h3 className="font-serif-luxe text-xl">{p.name}</h3>
                  <p className="gold-text font-serif-luxe text-2xl mt-2">Location : {p.price_label || `${Math.round(p.price_ttc)} € TTC`}</p>
                  <p className="text-xs text-white/60 mt-1 border-t border-white/10 pt-2" data-testid={`loc-pkg-deposit-${p.id}`}>{p.deposit_amount ? `Dépôt de garantie : ${Math.round(p.deposit_amount)} €` : "Dépôt de garantie : montant communiqué lors du devis"}</p>
                  {p.subtitle && <p className="text-xs text-white/55 mt-2">{p.subtitle}</p>}
                  <ul className="mt-4 space-y-1.5">{(p.features || []).map((f) => <li key={f} className="flex gap-2 text-[13px] text-white/70"><Check size={13} className="text-[#DCB457] mt-0.5 shrink-0" />{f}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          <List title="Caractéristiques" items={it.features} testid="loc-item-features" />
          <List title="Accessoires compris" items={it.included_accessories} testid="loc-item-included" />
          <List title="Accessoires en option" items={it.optional_accessories_detail} testid="loc-item-options" />
        </div>
        {(it.special_conditions || it.consumables || it.extra_info) && (
          <div className="card-luxe p-6 mt-10 text-sm text-white/65 space-y-2" data-testid="loc-item-conditions">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#DCB457]">Conditions principales</p>
            {it.special_conditions && <p className="whitespace-pre-line">{it.special_conditions}</p>}
            {it.consumables && <p>Consommables : {it.consumables}</p>}
            {it.extra_info && <p className="whitespace-pre-line">{it.extra_info}</p>}
          </div>
        )}
      </section>
      {it.related?.length > 0 && (
        <section className="py-16 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15" data-testid="loc-item-related">
          <div className="max-w-[1300px] mx-auto"><h2 className="font-serif-luxe text-3xl">Vous pourriez également avoir besoin de…</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">{it.related.map((r) => <ItemCard key={r.id} i={r} />)}</div></div>
        </section>
      )}
      <section id="demande" className="py-16 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <h2 className="font-serif-luxe text-3xl md:text-4xl">Demander un devis — {it.name}</h2>
        {all.length > 0 && <RentalRequestForm items={all} preselect={it.id} />}
      </section>
    </>
  );
}
